import Loading from 'components/shared/Loading';
import moment from 'moment';
import * as React from 'react';
import Loadable from 'react-loadable';
import { FormGroup, Input, Label } from 'reactstrap';
import { localMoment } from 'util/Time';
import { MultipleDateRangePresets } from '../providers/Constants';
import { RangedPresetValue, SingleRangedPresetValueFilterEditProps } from '../providers/Types';
import FilterEditRequiredValidation from './FilterEditRequiredValidation';
import { FiltersListAddButton } from './index';

const DateRangePicker = Loadable({
	loader: () =>
		import(/* webpackChunkName: "date-range-picker-component" */ 'react-dates/lib/components/DateRangePicker'),
	loading: Loading,
});

interface SingleDateRangePresetFilterEditState {
	currentValue: string;
	customFromValue: moment.Moment | null;
	customToValue: moment.Moment | null;
	focusedInput: 'startDate' | 'endDate' | null;
	displayValidationError: boolean;
}

/**
 * Configures single date range within a filter
 */
class SingleDateRangePresetFilterEdit extends React.PureComponent<
	SingleRangedPresetValueFilterEditProps,
	SingleDateRangePresetFilterEditState
> {
	constructor(props: Readonly<SingleRangedPresetValueFilterEditProps>) {
		super(props);

		this.state = {
			currentValue: '',
			displayValidationError: false,
			focusedInput: null,
			customFromValue: null,
			customToValue: null,
		};
	}

	/**
	 * Update local state when the controlled datepicker is updated
	 *
	 */
	private handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ currentValue: e.currentTarget.value });
	};

	/**
	 * sets teh selected range to the state and handles the search.
	 */
	private handleSearch = () => {
		const currentValue = this.state.currentValue;
		let currentValueDateRange: RangedPresetValue;

		if (currentValue.toString() === 'custom') {
			currentValueDateRange = {
				fromValue: this.state.customFromValue,
				toValue: this.state.customToValue,
			};
		} else {
			const presetFn = MultipleDateRangePresets[currentValue.toString()];
			if (!presetFn) {
				console.warn('Preset not found:', currentValue);
				return;
			}

			currentValueDateRange = presetFn();
		}

		const isEmpty = currentValueDateRange.fromValue === null && currentValueDateRange.toValue === null;
		const validationError = !this.props.filter.nullable && isEmpty;

		this.setState({
			displayValidationError: validationError,
		});

		this.setState({
			displayValidationError: validationError,
		});

		if (!validationError) {
			this.props.onChange(currentValueDateRange, false);
		}
	};

	/**
	 * Allow users to hit enter to select the date range and trigger the search.
	 *
	 */
	private handleOnKeyDown = (e: React.KeyboardEvent) => {
		if (e.which === 13) {
			e.preventDefault();
			this.handleSearch();
		}
	};

	public render() {
		const availableDateRanges = Object.keys(MultipleDateRangePresets);
		return (
			<FormGroup>
				<Label for="date-select" className="visually-hidden">
					Select date
				</Label>
				<Input
					id="date-select"
					data-testid="data-select-multiple"
					type="select"
					autoFocus
					value={this.state.currentValue}
					onKeyDown={this.handleOnKeyDown}
					onChange={this.handleOnChange}
					className=""
					autoComplete="off"
				>
					<option value="" />
					{availableDateRanges.map((preset, index) => {
						const presetValue = MultipleDateRangePresets[preset]();
						return (
							<option key={index} value={preset}>
								{presetValue.displayName || presetValue.preset}
							</option>
						);
					})}
					<option value="custom">Custom Date Range...</option>
				</Input>
				{this.state.currentValue === 'custom' ? (
					<DateRangePicker
						showDefaultInputIcon
						showClearDates
						small
						startDateId={'fromDate'}
						startDate={this.state.customFromValue}
						endDateId={'toDate'}
						endDate={this.state.customToValue}
						focusedInput={this.state.focusedInput}
						isOutsideRange={() => false}
						minimumNights={0}
						displayFormat={() => 'M/D/YYYY'}
						onDatesChange={({
							startDate,
							endDate,
						}: {
							startDate: moment.Moment;
							endDate: moment.Moment;
						}) => {
							this.setState({
								customFromValue: startDate ? localMoment(startDate).startOf('day') : null,
								customToValue: endDate ? localMoment(endDate).endOf('day') : null,
							});
						}}
						onFocusChange={(focusedInput) => {
							this.setState({ focusedInput });
						}}
					/>
				) : null}

				<div className="filters-popover__actions">
					<FiltersListAddButton onClick={this.handleSearch} />
				</div>

				{this.state.displayValidationError ? (
					<FilterEditRequiredValidation messageOverride="Field is required" />
				) : null}
				<p />
			</FormGroup>
		);
	}
}

export default SingleDateRangePresetFilterEdit;
